import { Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { FullPageLoading } from "components/states";

import { decideLanding, type LandingPath } from "./landing";
import { firestoreLandingSources } from "./landing-sources";

/**
 * Sends a signed-in person to their home page, replacing the current entry.
 *
 * It holds the loading screen until the destination is known, so a newcomer
 * never sees the empty My Events flash past on the way to All Events. The
 * redirect lives here, at `/`, and not inside the dashboard, so My Events
 * stays reachable from the rail for somebody with no events.
 */
export function LandingRedirect({ uid }: { uid: string }) {
  const [target, setTarget] = useState<LandingPath | null>(null);

  useEffect(() => {
    // Aborting stops the request listener and its timer, not just the
    // state update, when the person navigates away mid-wait.
    const controller = new AbortController();
    setTarget(null);
    void decideLanding(firestoreLandingSources(uid), controller.signal).then(
      (path) => {
        if (!controller.signal.aborted) setTarget(path);
      },
    );
    return () => controller.abort();
  }, [uid]);

  if (target === null) return <FullPageLoading />;
  return <Navigate to={target} replace />;
}
